/**
 * Regelversion 7: Parteispenden während der Legislatur.
 *
 *  - Kleinspenden kommen von selbst in die Wahlkampfkasse.
 *  - Ab und zu bietet jemand eine Großspende an. Ihr entscheidet: annehmen, nur einen Teil oder ablehnen.
 *  - Große Summen wecken Misstrauen. Je mehr ihr annehmt, desto eher deckt die Presse eine Spendenaffäre auf,
 *    die Opposition setzt einen Untersuchungsausschuss durch, Zustimmung und ein Teil des Geldes sind weg.
 *  - Beim nächsten Wahlkampf steht das Angesparte als „Spenden aus der Legislatur“ in der Wahlkampfkasse.
 * Nur Einzelspieler (im Mehrspieler bleibt alles wie bisher).
 */
import { OUTLETS } from '../content/parties';
import { rnd } from './rng';
import { S, log } from './state';
import { clamp, de } from './util';
import { lvlHas } from './level12';
import { since } from './version';
import { press7 } from './news7';
import { forceUa } from './opp5';

export type Don = { pot: number; big: { amt: number; by: string; m: number }[]; heat: number; hit?: number };
export const donOn = (): boolean => since(7) && !S.mp && lvlHas('don'); // Version 12: ab Stufe 2
const D = (): Don => (S.don ??= { pot: 0, big: [], heat: 0 });
const r1 = (x: number) => Math.round(x * 10) / 10;

const DONORS = [
  'Ein Immobilienunternehmer aus Hamburg', 'Die Erbin einer Industriellenfamilie', 'Ein Verband der Automobilindustrie',
  'Ein Tech-Investor aus München', 'Ein Bauunternehmer aus Sachsen', 'Ein Logistikkonzern', 'Eine Stiftung eines Familienunternehmens',
];
export const DON_OPTS: [string, string][] = [
  ['Annehmen', 'Das ganze Geld für den nächsten Wahlkampf. Große Summen fallen auf und können zur Affäre werden.'],
  ['Nur 0,5 Mio € annehmen', 'Ein Teil des Geldes, kaum Risiko.'],
  ['Ablehnen', 'Kein Geld, aber ein kleines Plus an Glaubwürdigkeit.'],
];

/** Wie leicht kommt die Partei an Spenden? Mehr bei wirtschaftsfreundlichem Programm und guter Stimmung bei Arbeitgebern. */
const bizK = () => clamp(1 + (S.you?.buero ?? 0) * .15 - (S.you?.steuern ?? 0) * .12 + ((S.lob?.arbg ?? 50) - 50) / 100, .5, 1.6);

/** Monatlich. Gibt true zurück, wenn der Spieler etwas entscheiden muss. */
export function donTick(): boolean {
  if (!donOn() || S.phase !== 'govern') return false;
  const d = D();
  d.heat *= .985;
  // Kleinspenden
  if (rnd() < .06) { const a = r1(.1 + rnd() * .3 * bizK()); d.pot = r1(d.pot + a); log(`Kleinspenden: ${de(a, 1)} Mio € für die Wahlkampfkasse.`); }
  // Spendenaffäre
  const total = d.big.reduce((s, b) => s + b.amt, 0);
  if (total >= .8 && rnd() < d.heat * .05) { affair(total); return true; }
  // Angebot einer Großspende
  if (!S.donOffer && !S.dec && !S.crisis && !S.oppMove && !S.scandal && rnd() < .022 * bizK()) {
    S.donOffer = { by: DONORS[Math.floor(rnd() * DONORS.length)], amt: r1(.4 + rnd() * 2.1), m: S.m };
    log(`${S.donOffer.by} bietet eurer Partei eine Spende über ${de(S.donOffer.amt, 1)} Mio € an.`);
    return true;
  }
  return false;
}

function affair(total: number) {
  const d = D(), loss = r1(clamp(1 + total * .5, 1, 4)), back = r1(Math.min(d.pot, total * .5));
  S.basis -= loss; d.pot = r1(d.pot - back); d.heat = 0; d.hit = (d.hit || 0) + 1;
  const by = d.big[d.big.length - 1]?.by || 'Großspender';
  d.big = [];
  log(`Spendenaffäre: Recherchen zeigen, wie eng ${S.name} mit Großspendern verbandelt ist. Ihr verliert an Zustimmung${back > 0 ? ` und zahlt ${de(back, 1)} Mio € zurück` : ''}.`, 'bad');
  press7(OUTLETS[0].n, `Spendenaffäre: ${by} und ${total >= 2 ? 'Millionen' : 'hohe Summen'} für ${S.name}`, -2);
  forceUa('Die Spendenaffäre', 'Wer hat was für die Spenden bekommen? Die Opposition will die Geldflüsse an die Regierungspartei aufklären.', .6);
}

/** Antwort auf ein Spendenangebot. */
export function donAnswer(i: number) {
  const O = S.donOffer; if (!O || !donOn()) return;
  const d = D(), o = clamp(Math.round(i), 0, 2);
  if (o === 0) { d.pot = r1(d.pot + O.amt); d.big.push({ amt: O.amt, by: O.by, m: S.m }); d.heat += O.amt * .12 + (O.amt >= 1.5 ? .1 : 0); log(`Ihr nehmt die Spende über ${de(O.amt, 1)} Mio € an.`); }
  if (o === 1) { const a = Math.min(.5, O.amt); d.pot = r1(d.pot + a); d.heat += .02; log(`Ihr nehmt ${de(a, 1)} Mio € der angebotenen Spende an.`); }
  if (o === 2) { S.basis += .2; press7(OUTLETS[2].n, `${S.name} lehnt Großspende ab`, .3); log('Ihr lehnt die Spende ab.', 'good'); }
  S.donOffer = null;
}

/** Wahlkampfbeginn: Angespartes in die Wahlkampfkasse. */
export function donToCampaign(fund: { total: number; parts: { n: string; v: number; d: string }[] }) {
  if (!donOn() || !S.don || S.don.pot <= 0) return;
  const v = Math.round(S.don.pot * 2) / 2;
  if (v > 0) { fund.parts.push({ n: 'Spenden aus der Legislatur', v, d: 'in den vergangenen Jahren angespart' }); fund.total += v; }
  S.don.pot = 0;
}
